import { useRef, useState } from "react";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IAttempt } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getAttemptUUID = createUUID();
const attemptOpenUUID = createUUID();
const attemptCloseUUID = createUUID();
const attemptPendingUUID = createUUID();

export const useWorkOrderAttemptDrawerViewModel = ({
  listAttempt,
  onClose,
  onAttemptUpdated,
}: {
  listAttempt: IAttempt;
  onClose: () => void;
  onAttemptUpdated?: (attempt: IAttempt) => void;
}) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const didSendRef = useRef<{ [index: string]: boolean }>({});
  const [attempt, setAttempt] = useState<IAttempt | null>(null);

  const getAttempt = () => {
    stateManager.abort(getAttemptUUID);
    stateManager.run(async () => {
      const { status, data } = await taskService.attempts.get(listAttempt.id);

      if (status === HTTP_STATUSES.OK) {
        setAttempt(data);
      }
    }, getAttemptUUID);
  };

  const handleAttemptOpen = () => {
    if (didSendRef.current.attemptOpen) {
      return;
    }

    didSendRef.current.attemptOpen = true;
    stateManager.abort(attemptOpenUUID);
    stateManager.run(async () => {
      const { status, data } = await taskService.attempts.handleOpen(
        listAttempt.id,
        { modifiedOn: listAttempt.modifiedOn, returnAttempt: true }
      );

      if (status === HTTP_STATUSES.OK) {
        setAttempt(data);
        if (onAttemptUpdated) {
          onAttemptUpdated(data);
        }
      }

      didSendRef.current.attemptOpen = false;
    }, attemptOpenUUID);
  };

  const handleAttemptClose = () => {
    if (didSendRef.current.attemptClose) {
      return;
    }

    didSendRef.current.attemptClose = true;
    stateManager.abort(attemptCloseUUID);
    stateManager.run(async () => {
      const { status, data } = await taskService.attempts.handleClose(
        listAttempt.id,
        {
          modifiedOn: attempt?.modifiedOn ?? listAttempt.modifiedOn,
          returnAttempt: true,
        }
      );

      if (status === HTTP_STATUSES.OK) {
        if (onAttemptUpdated) {
          onAttemptUpdated(data);
        }
      }

      didSendRef.current.attemptClose = false;
    }, attemptCloseUUID);
  };

  const handleAttemptPending = () => {
    stateManager.abort(attemptPendingUUID);
    stateManager.run(async () => {
      const { status } = await taskService.attempts.handlePending(
        listAttempt.id,
        { modifiedOn: attempt?.modifiedOn || listAttempt.modifiedOn }
      );

      if (status === HTTP_STATUSES.NO_CONTENT) {
        onClose();
      }
    }, attemptPendingUUID);
  };

  return {
    isLoading,
    attempt,
    steps: attempt?.steps ?? [],
    stateManager,
    getAttempt,
    handleAttemptOpen,
    handleAttemptClose,
    handleAttemptPending,
  };
};
